import { useLocation } from "@solidjs/router";
import {
  type Accessor,
  createContext,
  createSignal,
  onMount,
  type ParentComponent,
  type Setter,
  useContext,
} from "solid-js";
import { VIDEOS, type VideoId } from "~/components/marketing/content";

/** `fromLink` marks playback that no click started, so it has to begin muted. */
export type ActiveVideo = { id: VideoId; fromLink: boolean };

type Slot = { id: VideoId; el: HTMLElement };

type MarketingVideo = {
  active: Accessor<ActiveVideo | undefined>;
  /**
   * Call from a click handler: playback starts with sound inside the same gesture. When the video
   * is already the active one, it scrolls its slot into view instead.
   */
  play: (id: VideoId) => void;
  close: () => void;
  slot: Accessor<Slot | undefined>;
  setSlot: Setter<Slot | undefined>;
};

const MarketingVideoContext = createContext<MarketingVideo>();

export const useMarketingVideo = (): MarketingVideo => {
  const ctx = useContext(MarketingVideoContext);
  if (!ctx) throw new Error("useMarketingVideo must be used inside MarketingVideoProvider");
  return ctx;
};

const isVideoId = (id: unknown): id is VideoId => typeof id === "string" && Object.hasOwn(VIDEOS, id);

// The demo was `demo` before builder-demo needed telling apart from it, and links with that id are out there.
const VIDEO_ALIASES: Record<string, VideoId | undefined> = { demo: "exec-demo" };

/**
 * Owns the one video the marketing pages play. It sits in the layout, so a video keeps playing
 * across client-side navigation between marketing pages.
 */
export const MarketingVideoProvider: ParentComponent = (props) => {
  const location = useLocation();
  const [active, setActive] = createSignal<ActiveVideo>();
  const [slot, setSlot] = createSignal<Slot>();

  // Read once: the params stay in the URL (dropping landing or s would send a signed-in visitor
  // to the app on refresh), so a later navigation must not reopen a video the visitor closed.
  onMount(() => {
    const linked =
      location.query.video ??
      (location.pathname === "/" && (location.query.landing === "demo" ? "demo" : location.query.s));
    const id = (typeof linked === "string" && VIDEO_ALIASES[linked]) || linked;
    if (isVideoId(id)) setActive({ id, fromLink: true });
  });

  return (
    <MarketingVideoContext.Provider
      value={{
        active,
        play: (id) => {
          // Already playing (docked, say): bring its slot back into view rather than restart it.
          if (active()?.id === id) return slot()?.el.scrollIntoView({ block: "nearest" });
          setActive({ id, fromLink: false });
        },
        close: () => setActive(undefined),
        slot,
        setSlot,
      }}
    >
      {props.children}
    </MarketingVideoContext.Provider>
  );
};
